import { FormEvent, useState } from 'react';
import { Link } from 'react-router-dom';
import { WAITLIST_PAGE } from '../../data/waitlistPage';
import type { WaitlistInterest } from '../../data/site';
import { submitWaitlist } from '../../lib/waitlist';
import { SiteButton } from '../site/SiteButton';

function InterestIcon({ icon }: { icon: string }) {
  const common = { viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 1.75 };

  switch (icon) {
    case 'investor':
      return (
        <svg {...common} aria-hidden>
          <path d="M12 3v18M8 7h8M9 11h6" strokeLinecap="round" />
        </svg>
      );
    case 'entrepreneur':
      return (
        <svg {...common} aria-hidden>
          <path d="M4 18l6-8 4 5 6-9" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    case 'building':
      return (
        <svg {...common} aria-hidden>
          <path d="M4 20h16M6 20V8l6-4 6 4v12" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M10 12h4v8h-4z" />
        </svg>
      );
    case 'wallet':
      return (
        <svg {...common} aria-hidden>
          <rect x="3" y="6" width="18" height="14" rx="2" />
          <path d="M3 10h18M16 14h2" strokeLinecap="round" />
        </svg>
      );
    case 'pool':
      return (
        <svg {...common} aria-hidden>
          <circle cx="8" cy="9" r="2.5" />
          <circle cx="16" cy="9" r="2.5" />
          <path d="M4 18c0-2.2 1.8-4 4-4M20 18c0-2.2-1.8-4-4-4M10.5 14.5h3" strokeLinecap="round" />
        </svg>
      );
    default:
      return (
        <svg {...common} aria-hidden>
          <circle cx="12" cy="12" r="8" />
          <path d="M12 8v4l3 2" strokeLinecap="round" />
        </svg>
      );
  }
}

export function WaitlistForm() {
  const { form, sidebar } = WAITLIST_PAGE;
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [interest, setInterest] = useState<WaitlistInterest | ''>('');
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState('');

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();

    if (!name.trim() || !email.trim()) {
      setStatus('error');
      setMessage('Please fill in your name and email.');
      return;
    }

    if (!email.includes('@') || !email.includes('.')) {
      setStatus('error');
      setMessage('Please enter a valid email address.');
      return;
    }

    if (!interest) {
      setStatus('error');
      setMessage('Please select what brings you to Hello Gwiji.');
      return;
    }

    if (!termsAccepted) {
      setStatus('error');
      setMessage('Please accept the Terms and Conditions and Privacy Policy.');
      return;
    }

    setStatus('loading');
    setMessage('Submitting...');

    const result = await submitWaitlist({
      name: name.trim(),
      email: email.trim(),
      interest,
      termsAccepted: true,
      interestLabel: sidebar.interests.find((item) => item.value === interest)?.title ?? null,
    });

    if (result.ok) {
      setStatus('success');
      setMessage(result.message);
      setName('');
      setEmail('');
      setInterest('');
      setTermsAccepted(false);
      return;
    }

    setStatus('error');
    setMessage(result.message);
  }

  return (
    <div className="waitlist-form-card reveal-item">
      <span className="waitlist-form-card__tag">{form.tag}</span>
      <h2 className="waitlist-form-card__title">{form.title}</h2>
      <p className="waitlist-form-card__description paragraph-02 text-dark-grey-two">{form.description}</p>

      <form className="waitlist-form" onSubmit={handleSubmit} noValidate>
        <div className="waitlist-form__step">
          <p className="waitlist-form__step-label">
            <span className="waitlist-form__step-num">01</span>
            Your details
          </p>
          <div className="waitlist-form__row">
            <div className="waitlist-form__group">
              <label className="waitlist-form__label" htmlFor="waitlist-name">
                {form.nameLabel}
              </label>
              <input
                id="waitlist-name"
                className="waitlist-form__input"
                type="text"
                name="name"
                autoComplete="name"
                placeholder={form.namePlaceholder}
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </div>
            <div className="waitlist-form__group">
              <label className="waitlist-form__label" htmlFor="waitlist-email">
                {form.emailLabel}
              </label>
              <input
                id="waitlist-email"
                className="waitlist-form__input"
                type="email"
                name="email"
                autoComplete="email"
                placeholder={form.emailPlaceholder}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
          </div>
        </div>

        <fieldset className="waitlist-form__step waitlist-form__interest">
          <legend className="waitlist-form__step-label">
            <span className="waitlist-form__step-num">02</span>
            {form.interestLabel}
          </legend>
          <div className="waitlist-interest-list">
            {sidebar.interests.map((option, index) => {
              const selected = interest === option.value;
              return (
                <label
                  key={option.value}
                  className={`waitlist-interest-row${selected ? ' is-selected' : ''}`}
                >
                  <input
                    type="radio"
                    name="interest"
                    value={option.value}
                    checked={selected}
                    onChange={() => setInterest(option.value)}
                    className="waitlist-interest-row__input"
                  />
                  <span className="waitlist-interest-row__index" aria-hidden>
                    {String(index + 1).padStart(2, '0')}
                  </span>
                  <span className="waitlist-interest-row__icon" aria-hidden>
                    <InterestIcon icon={option.icon} />
                  </span>
                  <span className="waitlist-interest-row__copy">
                    <span className="waitlist-interest-row__title">{option.title}</span>
                    <span className="waitlist-interest-row__subtitle">{option.subtitle}</span>
                  </span>
                  <span className="waitlist-interest-row__mark" aria-hidden />
                </label>
              );
            })}
          </div>
        </fieldset>

        <div className="waitlist-form__step waitlist-form__step--submit">
          <p className="waitlist-form__step-label">
            <span className="waitlist-form__step-num">03</span>
            Confirm and join
          </p>

          <label className="waitlist-form__terms">
            <input
              type="checkbox"
              name="termsAccepted"
              checked={termsAccepted}
              onChange={(e) => setTermsAccepted(e.target.checked)}
              required
            />
            <span>
              {form.termsLabel}{' '}
              <Link to={form.termsLink} target="_blank" rel="noopener noreferrer">
                Terms and Conditions
              </Link>{' '}
              and{' '}
              <Link to={form.privacyLink} target="_blank" rel="noopener noreferrer">
                Privacy Policy
              </Link>
              .
            </span>
          </label>

          <div className="waitlist-form__actions">
            <SiteButton type="submit" variant="dark" disabled={status === 'loading'}>
              {status === 'loading' ? form.submittingLabel : form.submitLabel}
            </SiteButton>
          </div>

          {message ? (
            <p
              className={`waitlist-form__msg${
                status === 'success' ? ' waitlist-form__msg--success' : ''
              }${status === 'error' ? ' waitlist-form__msg--error' : ''}${
                status === 'loading' ? ' waitlist-form__msg--loading' : ''
              }`}
              role={status === 'success' || status === 'error' ? 'alert' : 'status'}
            >
              {message}
            </p>
          ) : null}
        </div>
      </form>
    </div>
  );
}
